// @ts-nocheck
import { useState, useEffect } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Skeleton } from './ui/skeleton';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { 
  ArrowLeft,
  Calendar,
  Building,
  Mail,
  Phone,
  Globe,
  Users,
  MessageSquare,
  Lightbulb,
  Eye,
  Link,
  Edit,
  Save,
  X,
  Activity,
  RefreshCw,
  AlertCircle,
  User
} from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { supabase } from '@product-radar/shared';
import { useWorkspace, useRegionalSettings } from '@/contexts/context';
import { toast } from 'sonner';
import { validateClientNumericFields } from '@/lib/client-validation';
import { AuditLog } from './audit-log';
import { formatCurrency, formatDate } from '@/lib/formatting';

interface ClientDetailProps {
  clientId: string;
  onBack: () => void;
  onNavigateToFeedback?: (feedbackId: string) => void;
  onNavigateToIdea?: (ideaId: string) => void;
}

export function ClientDetail({ clientId, onBack, onNavigateToFeedback, onNavigateToIdea }: ClientDetailProps) {
  const { workspace, membership } = useWorkspace();
  const regionalSettings = useRegionalSettings();
  const [client, setClient] = useState<any>(null);
  const [linkedFeedback, setLinkedFeedback] = useState<any[]>([]);
  const [linkedIdeas, setLinkedIdeas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [workspaceMembers, setWorkspaceMembers] = useState<{ user_id: string; display_name: string; email: string }[]>([]);
  const [editedClient, setEditedClient] = useState<any>({
    name: '',
    industry: '',
    size: '',
    status: 'active',
    arr: '',
    health_score: '',
    primary_contact: '',
    account_owner: '',
    account_owner_user_id: null as string | null,
    notes: ''
  });

  useEffect(() => {
    if (workspace && clientId) {
      loadClient();
      loadWorkspaceMembers();
    }
  }, [workspace, clientId]);

  const loadWorkspaceMembers = async () => {
    if (!workspace) return;
    try {
      const { data, error } = await supabase.rpc('get_workspace_member_names_for_lookup', {
        p_workspace_id: workspace.id
      });
      if (error) throw error;
      setWorkspaceMembers(data || []);
    } catch (err) {
      console.error('Error loading workspace members:', err);
    }
  };

  const loadClient = async () => {
    if (!workspace) return;

    try {
      setLoading(true);

      // Load client
      const { data: clientData, error: clientError } = await supabase
        .from('clients')
        .select('*')
        .eq('id', clientId)
        .eq('workspace_id', workspace.id)
        .single();

      if (clientError) throw clientError;

      setClient(clientData);
      setEditedClient({
        name: clientData.name || '',
        industry: clientData.industry || '',
        size: clientData.size || '',
        status: clientData.status || 'active',
        arr: clientData.arr ? String(clientData.arr) : '',
        health_score: clientData.health_score ? String(clientData.health_score) : '',
        primary_contact: clientData.primary_contact || '',
        account_owner: clientData.account_owner || '',
        account_owner_user_id: clientData.account_owner_user_id || null,
        notes: clientData.notes || ''
      });

      // Load linked feedback
      const { data: feedbackData, error: feedbackError } = await supabase
        .from('feedback')
        .select('id, content, sentiment, source, contributor, status, created_at')
        .eq('client_id', clientId)
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: false });

      if (!feedbackError && feedbackData) {
        setLinkedFeedback(feedbackData);
      }

      // Load linked ideas
      const { data: ideaLinks, error: ideaError } = await supabase
        .from('client_ideas')
        .select(`
          idea:idea_id(id, title, status, priority),
          priority
        `)
        .eq('client_id', clientId);

      if (!ideaError && ideaLinks) {
        const mappedIdeas = ideaLinks.map((il: any) => ({ ...il.idea, request_priority: il.priority })).filter(Boolean);
        setLinkedIdeas(mappedIdeas);
      }

      // Also load ideas linked through feedback
      const { data: feedbackIdeaLinks, error: feedbackIdeaError } = await supabase
        .from('feedback')
        .select(`
          id,
          feedback_ideas(
            idea:idea_id(id, title, status, priority)
          )
        `)
        .eq('client_id', clientId)
        .eq('workspace_id', workspace.id);

      console.log('Feedback-linked ideas query result:', { feedbackIdeaLinks, feedbackIdeaError });

      // Merge both sets of ideas and deduplicate
      const allIdeas = [...(ideaLinks?.map((il: any) => ({ ...il.idea, request_priority: il.priority })).filter(Boolean) || [])];
      
      if (feedbackIdeaLinks) {
        feedbackIdeaLinks.forEach((feedback: any) => {
          if (feedback.feedback_ideas) {
            feedback.feedback_ideas.forEach((fi: any) => {
              if (fi.idea && !allIdeas.find((idea: any) => idea.id === fi.idea.id)) {
                allIdeas.push(fi.idea);
              }
            });
          }
        });
      }

      console.log('All linked ideas (merged):', allIdeas);
      setLinkedIdeas(allIdeas);

    } catch (error: any) {
      console.error('Error loading client:', error);
      toast.error('Failed to load client details', {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const getSentimentBadge = (sentiment: string) => {
    const styles = {
      'positive': 'bg-green-50 text-green-600 border-green-200',
      'negative': 'bg-red-50 text-red-600 border-red-200',
      'neutral': 'bg-blue-50 text-blue-600 border-blue-200'
    };
    return styles[sentiment as keyof typeof styles] || 'bg-gray-50 text-gray-600 border-gray-200';
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      'active': 'bg-green-50 text-green-600 border-green-200',
      'inactive': 'bg-yellow-50 text-yellow-600 border-yellow-200',
      'churned': 'bg-red-50 text-red-600 border-red-200'
    };
    return styles[status as keyof typeof styles] || 'bg-gray-50 text-gray-600 border-gray-200';
  };

  const getHealthScoreColor = (score?: number) => {
    if (!score) return 'bg-gray-400';
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const handleEditClick = () => {
    if (client.crm_synced) {
      toast.error('Cannot edit synced client', {
        description: 'This client is synced from a CRM integration and cannot be edited manually.'
      });
      return;
    }
    setEditedClient({
      name: client.name || '',
      industry: client.industry || '',
      size: client.size || '',
      status: client.status || 'active',
      arr: client.arr ? String(client.arr) : '',
      health_score: client.health_score ? String(client.health_score) : '',
      primary_contact: client.primary_contact || '',
      account_owner: client.account_owner || '',
      account_owner_user_id: client.account_owner_user_id || null,
      notes: client.notes || ''
    });
    setEditing(true);
  };

  const handleCancelEdit = () => {
    setEditedClient({
      name: client.name || '',
      industry: client.industry || '',
      size: client.size || '',
      status: client.status || 'active',
      arr: client.arr ? String(client.arr) : '',
      health_score: client.health_score ? String(client.health_score) : '',
      primary_contact: client.primary_contact || '',
      account_owner: client.account_owner || '',
      account_owner_user_id: client.account_owner_user_id || null,
      notes: client.notes || ''
    });
    setEditing(false);
  };

  const handleSaveChanges = async () => {
    if (!workspace) return;

    // Validation
    if (!editedClient.name?.trim()) {
      toast.error('Validation Error', {
        description: 'Client name is required'
      });
      return;
    }

    const { arr: parsedArr, health_score: parsedHealth, error: validationError } = validateClientNumericFields(editedClient.arr, editedClient.health_score);
    if (validationError) {
      toast.error('Validation Error', { description: validationError });
      return;
    }

    try {
      const { error } = await supabase
        .from('clients')
        .update({
          name: editedClient.name,
          industry: editedClient.industry || null,
          size: editedClient.size || null,
          status: editedClient.status,
          arr: parsedArr,
          health_score: parsedHealth,
          primary_contact: editedClient.primary_contact || null,
          account_owner: editedClient.account_owner || null,
          account_owner_user_id: editedClient.account_owner_user_id || null,
          notes: editedClient.notes || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', clientId)
        .eq('workspace_id', workspace.id);

      if (error) throw error;

      // Create audit log
      await supabase.from('audit_logs').insert({
        workspace_id: workspace.id,
        user_id: membership?.user_id,
        action: 'update',
        entity_type: 'client',
        entity_id: clientId,
        details: { updated_fields: ['name', 'industry', 'size', 'status', 'arr', 'health_score', 'primary_contact', 'notes'] }
      });

      // Reload client data
      await loadClient();
      setEditing(false);
      
      toast.success('Client updated successfully');
    } catch (error: any) {
      console.error('Error updating client:', error);
      toast.error('Failed to update client', {
        description: error.message
      });
    }
  };

  // Check if user has permission to edit
  const canEdit = membership?.role && ['admin', 'product_manager', 'contributor'].includes(membership.role);
  const isCrmSynced = client?.crm_synced || client?.integration_source;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-1">
            <Skeleton className="h-96" />
          </div>
          <div className="col-span-2">
            <Skeleton className="h-96" />
          </div>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <p className="text-muted-foreground mb-4">Client not found</p>
        <Button onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Clients
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between border-b pb-4">
        <div className="flex items-center space-x-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="h-9">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="h-8 w-px bg-border" />
          <div>
            <div className="flex items-center space-x-2">
              <h1 className="text-2xl">{client.name}</h1>
              <span className="text-sm text-muted-foreground">· ID: {client.id.substring(0, 8)}</span>
              {isCrmSynced && (
                <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200">
                  <RefreshCw className="h-3 w-3 mr-1" />
                  CRM Synced
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <Button variant="outline" size="sm" onClick={handleCancelEdit}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button size="sm" onClick={handleSaveChanges}>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
            </>
          ) : (
            <>
              {canEdit && !isCrmSynced && (
                <Button variant="outline" size="sm" onClick={handleEditClick}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
              {isCrmSynced && (
                <Button variant="ghost" size="sm" disabled title="Cannot edit synced clients">
                  <AlertCircle className="h-4 w-4 mr-2" />
                  Synced from CRM
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* CRM Sync Warning */}
      {isCrmSynced && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <RefreshCw className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm text-blue-900 font-medium">This client is synced from a CRM integration</p>
              <p className="text-xs text-blue-700 mt-1">Changes must be made in your CRM system. Data will sync automatically.</p>
            </div>
          </div>
        </div>
      )}

      {/* Edit Mode Banner */}
      {editing && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Edit className="h-4 w-4 text-blue-600" />
            <p className="text-sm text-blue-900">You are now editing this client. Make your changes below and click "Save Changes" when done.</p>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-3 gap-6">
        {/* Left column - Details */}
        <div className="col-span-1 space-y-6">
          <Card className={`p-6 ${editing ? 'border-blue-200 shadow-sm' : ''}`}>
            <div className="flex items-center space-x-2 mb-4">
              <Building className="h-5 w-5 text-muted-foreground" />
              <h3>Company Information</h3>
            </div>
            
            <div className="space-y-4">
              {/* Status */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Status</Label>
                {editing ? (
                  <Select 
                    value={editedClient.status} 
                    onValueChange={(value) => setEditedClient({ ...editedClient, status: value })}
                  >
                    <SelectTrigger className="border-blue-200 focus:border-blue-400">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="churned">Churned</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge className={getStatusBadge(client.status)}>
                    {client.status.charAt(0).toUpperCase() + client.status.slice(1)}
                  </Badge>
                )}
              </div>

              {/* Industry */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Industry</Label>
                {editing ? (
                  <Input
                    value={editedClient.industry}
                    onChange={(e) => setEditedClient({ ...editedClient, industry: e.target.value })}
                    placeholder="e.g., Technology, Healthcare"
                    className="border-blue-200 focus:border-blue-400"
                  />
                ) : (
                  <p className="text-sm">{client.industry || 'N/A'}</p>
                )}
              </div>

              {/* Size */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Company Size</Label>
                {editing ? (
                  <Select 
                    value={editedClient.size} 
                    onValueChange={(value) => setEditedClient({ ...editedClient, size: value })}
                  >
                    <SelectTrigger className="border-blue-200 focus:border-blue-400">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="small">Small (1-50)</SelectItem>
                      <SelectItem value="mid-market">Mid-Market (51-500)</SelectItem>
                      <SelectItem value="enterprise">Enterprise (500+)</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-sm">{client.size || 'N/A'}</p>
                )}
              </div>

              {/* ARR */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  {`Annual Recurring Revenue (${(regionalSettings.currency || 'USD').toUpperCase()})`}
                </Label>
                {editing ? (
                  <Input
                    type="number"
                    value={editedClient.arr}
                    onChange={(e) => setEditedClient({ ...editedClient, arr: e.target.value })}
                    placeholder="0"
                    className="border-blue-200 focus:border-blue-400"
                  />
                ) : (
                  <span className="font-medium">
                    {client.arr ? formatCurrency(client.arr, regionalSettings) : 'N/A'}
                  </span>
                )}
              </div>

              {/* Health Score */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Health Score</Label>
                {editing ? (
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={editedClient.health_score}
                    onChange={(e) => setEditedClient({ ...editedClient, health_score: e.target.value })}
                    placeholder="0-100"
                    className="border-blue-200 focus:border-blue-400"
                  />
                ) : (
                  client.health_score ? (
                    <div className="flex items-center space-x-2">
                      <div className={`h-2 w-2 rounded-full ${getHealthScoreColor(client.health_score)}`} />
                      <span className="font-medium">{client.health_score}%</span>
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">N/A</span>
                  )
                )}
              </div>

              {/* Primary Contact */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Primary Contact</Label>
                {editing ? (
                  <Input
                    value={editedClient.primary_contact}
                    onChange={(e) => setEditedClient({ ...editedClient, primary_contact: e.target.value })}
                    placeholder="Contact name or email"
                    className="border-blue-200 focus:border-blue-400"
                  />
                ) : (
                  <p className="text-sm">{client.primary_contact || 'N/A'}</p>
                )}
              </div>

              {/* Account Owner */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Account Owner</Label>
                {editing ? (
                  <div className="flex gap-1">
                    <Input
                      value={editedClient.account_owner}
                      onChange={(e) => setEditedClient({ ...editedClient, account_owner: e.target.value, account_owner_user_id: null })}
                      placeholder="Type name or select from workspace..."
                      className="flex-1 border-blue-200 focus:border-blue-400"
                    />
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="outline" size="icon" title="Select from workspace members">
                          <User className="h-4 w-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[280px] p-0" align="end">
                        <Command>
                          <CommandInput placeholder="Search members..." />
                          <CommandEmpty>No members found.</CommandEmpty>
                          <CommandGroup>
                            {workspaceMembers.map((m) => (
                              <CommandItem
                                key={m.user_id}
                                value={`${m.display_name} ${m.email}`}
                                onSelect={() => setEditedClient({ ...editedClient, account_owner: m.display_name || m.email, account_owner_user_id: m.user_id })}
                              >
                                <User className="h-4 w-4 mr-2" />
                                <span>{m.display_name || m.email}</span>
                                {m.email && m.display_name && (
                                  <span className="text-xs text-muted-foreground ml-1">({m.email})</span>
                                )}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                ) : (
                  <p className="text-sm">{client.account_owner || 'N/A'}</p>
                )}
              </div>

              {/* Created At */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Client Since</Label>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    {formatDate(client.created_at, regionalSettings)}
                  </span>
                </div>
              </div>
            </div>
          </Card>

          {/* Notes */}
          {(editing || client.notes) && (
            <Card className={`p-6 ${editing ? 'border-blue-200 shadow-sm' : ''}`}>
              <h3 className="mb-4">Notes</h3>
              {editing ? (
                <Textarea
                  value={editedClient.notes}
                  onChange={(e) => setEditedClient({ ...editedClient, notes: e.target.value })}
                  placeholder="Add notes about this client..."
                  rows={5}
                  className="border-blue-200 focus:border-blue-400"
                />
              ) : (
                <p className="text-sm whitespace-pre-wrap">{client.notes || 'No notes'}</p>
              )}
            </Card>
          )}

          {/* Stats Card */}
          <Card className="p-6">
            <h3 className="mb-4">Statistics</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-muted rounded">
                <div className="flex items-center space-x-2">
                  <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Feedback Items</span>
                </div>
                <span className="font-medium">{linkedFeedback.length}</span>
              </div>

              <div className="flex items-center justify-between p-3 bg-muted rounded">
                <div className="flex items-center space-x-2">
                  <Lightbulb className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Linked Ideas</span>
                </div>
                <span className="font-medium">{linkedIdeas.length}</span>
              </div>
            </div>
          </Card>
        </div>

        {/* Right column - Feedback and Ideas */}
        <div className="col-span-2 space-y-6">
          {/* Feedback Table */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
                <h3>Feedback ({linkedFeedback.length})</h3>
              </div>
            </div>

            {linkedFeedback.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      <th className="text-left p-3 text-xs uppercase text-muted-foreground">Content</th>
                      <th className="text-left p-3 text-xs uppercase text-muted-foreground">Source</th>
                      <th className="text-left p-3 text-xs uppercase text-muted-foreground">Sentiment</th>
                      <th className="text-left p-3 text-xs uppercase text-muted-foreground">Status</th>
                      <th className="text-left p-3 text-xs uppercase text-muted-foreground">Date</th>
                      <th className="text-left p-3 text-xs uppercase text-muted-foreground">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linkedFeedback.map((feedback: any) => (
                      <tr key={feedback.id} className="border-b hover:bg-muted/50 transition-colors">
                        <td className="p-3">
                          <div className="max-w-md">
                            <p className="text-sm line-clamp-2">{feedback.content}</p>
                            {feedback.author && (
                              <p className="text-xs text-muted-foreground mt-1">by {feedback.author}</p>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          <Badge variant="outline" className="text-xs">{feedback.source}</Badge>
                        </td>
                        <td className="p-3">
                          {feedback.sentiment && (
                            <Badge className={`${getSentimentBadge(feedback.sentiment)} text-xs`}>
                              {feedback.sentiment}
                            </Badge>
                          )}
                        </td>
                        <td className="p-3">
                          <Badge variant="secondary" className="text-xs">{feedback.status}</Badge>
                        </td>
                        <td className="p-3">
                          <span className="text-sm text-muted-foreground whitespace-nowrap">
                            {formatDate(feedback.created_at, regionalSettings)}
                          </span>
                        </td>
                        <td className="p-3">
                          {onNavigateToFeedback && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onNavigateToFeedback(feedback.id)}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 bg-muted/30 rounded-lg border-2 border-dashed">
                <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-sm text-muted-foreground">No feedback from this client yet</p>
              </div>
            )}
          </Card>

          {/* Ideas Table */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Lightbulb className="h-4 w-4 text-muted-foreground" />
                <h3>Linked Ideas ({linkedIdeas.length})</h3>
              </div>
            </div>

            {linkedIdeas.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      <th className="text-left p-3 text-xs uppercase text-muted-foreground">Title</th>
                      <th className="text-left p-3 text-xs uppercase text-muted-foreground">Status</th>
                      <th className="text-left p-3 text-xs uppercase text-muted-foreground">Priority</th>
                      <th className="text-left p-3 text-xs uppercase text-muted-foreground">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linkedIdeas.map((idea: any) => (
                      <tr key={idea.id} className="border-b hover:bg-muted/50 transition-colors">
                        <td className="p-3">
                          <p className="text-sm font-medium">{idea.title}</p>
                        </td>
                        <td className="p-3">
                          <Badge variant="secondary" className="text-xs">{idea.status}</Badge>
                        </td>
                        <td className="p-3">
                          {idea.priority && (
                            <div className="flex items-center space-x-1">
                              {[1, 2, 3, 4, 5].map((dot) => (
                                <div
                                  key={dot}
                                  className={`w-2 h-2 rounded-full ${
                                    dot <= idea.priority ? 'bg-[#245F73]' : 'bg-gray-200'
                                  }`}
                                />
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="p-3">
                          {onNavigateToIdea && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onNavigateToIdea(idea.id)}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 bg-muted/30 rounded-lg border-2 border-dashed">
                <Lightbulb className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-sm text-muted-foreground">No ideas linked to this client yet</p>
              </div>
            )}
          </Card>

          {/* Audit Log */}
          <Card className="p-6">
            <h3 className="mb-4">Activity Log</h3>
            <AuditLog recordId={clientId} tableName="clients" />
          </Card>
        </div>
      </div>
    </div>
  );
}